/**
 * Shared calendar primitives for DatePicker.tsx and DateRangePicker.tsx —
 * date parsing/formatting (local time, not UTC, to avoid off-by-one-day
 * bugs), the month-grid builder, and the outside-click/Escape-to-close
 * popover behavior both components share.
 */

import { useEffect, useMemo, useState, type RefObject } from 'react';

export const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export function parseISO(s?: string): Date | null {
  if (!s) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

export function toISO(d: Date): string {
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const da = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mo}-${da}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** Order two dates so the earlier one comes first. */
export function orderDates(a: Date, b: Date): [Date, Date] {
  return a <= b ? [a, b] : [b, a];
}

/**
 * Builds the visible month's day-cell grid (padded with `null` for the
 * leading/trailing blanks) and exposes prev/next month navigation.
 */
export function useMonthGrid(initial: Date) {
  const [view, setView] = useState<Date>(initial);

  const cells = useMemo<(Date | null)[]>(() => {
    const year = view.getFullYear();
    const month = view.getMonth();
    const startDow = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const out: (Date | null)[] = [];
    for (let i = 0; i < startDow; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(new Date(year, month, d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [view]);

  const prevMonth = () => setView((v) => new Date(v.getFullYear(), v.getMonth() - 1, 1));
  const nextMonth = () => setView((v) => new Date(v.getFullYear(), v.getMonth() + 1, 1));

  return { view, setView, cells, prevMonth, nextMonth };
}

/**
 * Closes an open popover on outside click or Escape. When the popover renders
 * in a portal (outside the wrap element's DOM subtree), pass its ref as
 * `popoverRef` so clicks inside the portal aren't treated as "outside".
 */
export function useClosePopoverOnOutsideOrEscape(
  open: boolean,
  wrapRef: RefObject<HTMLElement | null>,
  close: () => void,
  popoverRef?: RefObject<HTMLElement | null>,
) {
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      const inWrap = wrapRef.current?.contains(target);
      const inPopover = popoverRef?.current?.contains(target);
      if (!inWrap && !inPopover) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, wrapRef, close, popoverRef]);
}
